import gsap, {Circ} from 'gsap';
import {ScrollTrigger} from 'gsap/all';
import {isMobile} from '../helpers';

gsap.registerPlugin(ScrollTrigger);

const scrollAppear = () => {
  const SELECTOR = '.animate-scroll-appear';

  const targets = document.querySelectorAll(SELECTOR);

  targets.forEach((target) => {
    const animation = gsap.from(
      target,
      {
        y: 100,
        autoAlpha: 0,
        duration: 1,
        ease: Circ.easeOut,
        paused: true
      },
      0
    );

    ScrollTrigger.create({
      trigger: target,
      start: 'top bottom',
      onEnter: () => animation.play(),
      onEnterBack: () => animation.play(),
      onLeave: () => animation.reverse(),
      onLeaveBack: () => animation.reverse()
    });
  });
};

const scrollTransition = (scrollValue = 0) => {
  const CLASS = 'animate-scroll-transition';
  const CLASS_WRAPPER = 'animate-scroll-transition-wrapper';

  document
    .querySelectorAll(`.${CLASS}`)
    .forEach((target) => $(target).wrap(`<div class='${CLASS_WRAPPER}'></div>`));

  document.querySelectorAll(`.${CLASS_WRAPPER}`).forEach((wrapper) => {
    wrapper.style.height = wrapper.querySelector(`.${CLASS}`).offsetHeight - scrollValue + 'px';
  });

  document.querySelectorAll(`.${CLASS}`).forEach((target) => {
    gsap.to(target, {
      y: scrollValue,

      scrollTrigger: {
        trigger: target.parentElement,
        scrub: 0.5,
        start: 'top bottom',
        end: 'bottom top'
      }
    });
  });
};

const INVERSION_TRIGGERS = [
  '.animate-inverse-header',
  '.park-new__top ~ .pin-spacer',
  '.about-new__top + .pin-spacer'
];

const scrollToTransition = () => {
  const INVERSE_CLASS = 'scroll-to--inverse--animated';
  const BUTTON = '.scroll-to-top';
  const FOOTER = '.footer';

  setTimeout(() => {
    document.querySelectorAll(INVERSION_TRIGGERS.join(',')).forEach((trigger) =>
      gsap.from(trigger, {
        scrollTrigger: {
          start: 'top bottom',
          end: 'bottom bottom',
          trigger: trigger,
          toggleClass: {targets: BUTTON, className: INVERSE_CLASS},

          onLeave: () =>
            ScrollTrigger.isInViewport(document.querySelector(FOOTER))
              ? document.querySelector(BUTTON).classList.toggle(INVERSE_CLASS)
              : null
        }
      })
    );
  }, 150);
};

const headerTransition = () => {
  const INVERSE_CLASS = 'header--inverse--animated';
  const CLASS = '.header';

  setTimeout(() => {
    document.querySelectorAll(INVERSION_TRIGGERS.join(',')).forEach((trigger) => {
      gsap.from(trigger, {
        scrollTrigger: {
          start: 'top 40px',
          end: 'bottom 40px',
          trigger: trigger,
          toggleClass: {targets: CLASS, className: INVERSE_CLASS}
        }
      });
    });
  }, 150);
};

export const pressItemAppear = () => {
  const items = document.querySelectorAll(
    '.media-card, .press .news-card, .press-center .news-card, .press-center .gallery-card, .home .news-card'
  );

  ScrollTrigger.batch(items, {
    onEnter: (batch) => gsap.to(batch, {autoAlpha: 1, stagger: 0.1})
  });
};

const randomAppear = () => {
  const unshuffledItems = document.querySelectorAll('.home__project-tag');

  let shuffledItems = Array.from(unshuffledItems)
    .map((value) => ({value, sort: Math.random()}))
    .sort((a, b) => a.sort - b.sort)
    .map(({value}) => value);

  ScrollTrigger.batch(shuffledItems, {
    onEnter: (batch) => gsap.to(batch, {autoAlpha: 1, stagger: 0.075})
  });
};

export const panelsTransition = (contentSelector, pinSelector, isStatic = false) => {
  const container = document.querySelector(contentSelector);

  const scrollWidth = window.innerWidth - $(window).width();

  if (container) {
    gsap.to(container, {
      x: isStatic ? 0 : -1 * (container.scrollWidth - window.innerWidth + scrollWidth),
      ease: 'none',

      scrollTrigger: {
        trigger: document.querySelector(pinSelector),
        pin: true,
        start: 'center center',
        scrub: 0.3,
        end: () => '+=' + (container.scrollWidth - window.innerWidth)
      }
    });
  }
};

const parallaxTransition = () => {
  const pinContainer = document.querySelector('.about-new__info');
  const contentContainer = document.querySelector('.about-new__info-container');
  const images = document.querySelectorAll('.about-new__info-image');

  if (pinContainer && contentContainer) {
    gsap.to(contentContainer, {
      y: -1 * (contentContainer.scrollHeight - window.innerHeight),
      ease: 'none',

      scrollTrigger: {
        trigger: pinContainer,
        pin: true,
        start: 'center center',
        scrub: 0.3,
        end: () => '+=' + (contentContainer.scrollHeight - window.innerHeight)
      }
    });
  }

  const leftIndexes = [2, 4, 6, 9, 13];
  const rightIndexes = [3, 5, 10, 12, 14];

  images.forEach((image) => {
    const imageIndex = Math.abs(parseInt(image.className.slice(-2)));
    const isLeft = leftIndexes.includes(imageIndex);
    const isRight = rightIndexes.includes(imageIndex);

    gsap.from(image, {
      y: isLeft ? -200 : isRight ? 100 : 0,

      scrollTrigger: {
        trigger: image,
        start: 'top bottom',
        end: 'bottom top',
        scrub: 0.2,
        refreshPriority: -1
      }
    });
  });
};

const prompterTransition = () => {
  const pinContainerTrigger = document.querySelector('.about-new__goal');
  const pinContainer = document.querySelector('.about-new__goal-content');
  const texts = document.querySelectorAll('.about-new__goal-text');
  const textWrappers = document.querySelectorAll('.about-new__goal-text-wrapper');
  const textActiveClass = 'about-new__goal-text--active';
  const textHiddenClass = 'about-new__goal-text--hidden';
  const blockHeader = document.querySelector('.about-new__goal-header');

  if (pinContainer) {
    gsap.to(pinContainer, {
      y: -1 * (pinContainer.scrollHeight - window.innerHeight * 0.75),
      ease: 'none',

      scrollTrigger: {
        trigger: pinContainerTrigger,
        pin: true,
        start: 'center center',
        scrub: 0.3,
        end: () => '+=' + (pinContainer.scrollHeight - window.innerHeight * 0.75),
        onUpdate: () => {
          texts.forEach((text) => {
            if (
              blockHeader.getBoundingClientRect().bottom + window.innerHeight * 0.1 >
              text.parentElement.getBoundingClientRect().top
            ) {
              text.classList.remove(textActiveClass);
              text.classList.add(textHiddenClass);
            } else if (
              blockHeader.getBoundingClientRect().bottom + window.innerHeight * 0.4 >
              text.parentElement.getBoundingClientRect().top
            ) {
              text.classList.remove(textHiddenClass);
              text.classList.add(textActiveClass);
            } else {
              text.classList.remove(textActiveClass);
              text.classList.remove(textHiddenClass);
            }
          });
        }
      }
    });

    textWrappers.forEach((text, index) => {
      if (index < textWrappers.length - 1) {
        gsap.to(text, {
          y: () => window.innerHeight * 0.3,
          ease: 'none',

          scrollTrigger: {
            trigger: text,
            start: 'top center',
            end: () => '+=' + window.innerHeight * 0.3,
            invalidateOnRefresh: true,
            scrub: 0.3
          }
        });
      }
    });
  }
};

const aboutAnimation = () => {
  const pinTrigger = document.querySelector(
    '.about-new__top-img-wrapper, .park-new__top-img-wrapper, .legacy__top-img-wrapper'
  );
  const pinItem = document.querySelector(
    '.about-new__top-img, .park-new__top-img, .legacy__top-img'
  );

  if (pinTrigger) {
    gsap.to(pinItem, {
      ease: 'none',
      width: window.innerWidth,
      y: -120,

      scrollTrigger: {
        trigger: pinTrigger,
        pin: true,
        scrub: 0.3,
        start: 0,
        end: () => '+=' + window.innerHeight
      }
    });
  }

  const infoItem = document.querySelector(
    '.about-new__top-info, .park-new__top-info, .legacy__top-info'
  );
  const infoTrigger = document.querySelector(
    '.about-new__top-animation, .park-new__top-animation, .legacy__top-animation'
  );

  if (infoTrigger) {
    gsap.from(infoItem, {
      scrollTrigger: {
        trigger: infoItem,
        endTrigger: infoTrigger,
        pin: true,
        scrub: 0.3,
        start: 0,
        end: 'top center+=200px'
      }
    });
  }

  const info = document.querySelectorAll('.about-new__info-main');

  info.forEach((infoItem) => {
    gsap.to(infoItem, {
      autoAlpha: 0,
      y: () => window.innerHeight,
      ease: 'none',

      scrollTrigger: {
        trigger: infoItem,
        scrub: 0.3,
        refreshPriority: -1,
        invalidateOnRefresh: true,
        start: 'center center',
        end: () => '+=' + window.innerHeight
      }
    });
  });
};

const parkAnimation = () => {
  const firstPanel = document.querySelector('.park-new__place-panel');

  gsap.to(firstPanel, {
    autoAlpha: 0,
    x: window.innerWidth,
    ease: 'none',

    scrollTrigger: {
      trigger: firstPanel,
      start: 'center center',
      scrub: 0.3,
      end: () => '+=' + window.innerWidth
    }
  });

  const orderTexts = document.querySelectorAll('.park-new__place-round p');
  const container = document.querySelector('.park-new__place-container');

  orderTexts.forEach((text) => {
    const image = text.parentElement.querySelector('img');

    gsap
      .timeline()
      .from(text, {
        x: 200
      })
      .to(text, {
        x: -text.parentElement.offsetLeft,

        scrollTrigger: {
          start: () =>
            container.getBoundingClientRect().top +
            window.scrollY +
            text.parentElement.parentElement.offsetLeft +
            text.parentElement.offsetLeft -
            window.innerWidth,
          end: () => '+=' + window.innerWidth,
          scrub: 0.3
        }
      });

    gsap.to(image, {
      x: -text.parentElement.offsetLeft,

      scrollTrigger: {
        start: () =>
          container.getBoundingClientRect().top +
          window.scrollY +
          text.parentElement.parentElement.offsetLeft +
          text.parentElement.offsetLeft -
          window.innerWidth,
        end: () => '+=' + window.innerWidth,
        scrub: 0.3
      }
    });
  });

  const rounds = document.querySelectorAll('.park-new__place-round');

  rounds.forEach((round) => {
    const nextElementOffset = round.parentElement.nextElementSibling.offsetLeft;
    const roundOffset = round.parentElement.offsetLeft;
    const shift =
      nextElementOffset -
      roundOffset -
      Array.from(rounds).sort((a, b) => (a.clientWidth < b.clientWidth ? 1 : -1))[0].clientWidth -
      50;

    gsap.to(round, {
      x: shift,
      ease: 'none',

      scrollTrigger: {
        start: () => container.getBoundingClientRect().top + window.scrollY + roundOffset - 50,
        end: () => '+=' + shift,
        scrub: 0.3
      }
    });
  });

  const slowImages = Array.from(
    document.querySelectorAll(
      '.park-new__place-images-textblock, .park-new__place-images-1-collision, .park-new__place-images-2-collision-2'
    )
  );
  const fastImages = Array.from(
    document.querySelectorAll(
      '.park-new__place-images-2-collision-1, .park-new__place-images-2-collision-3, .park-new__place-images-3-collision img'
    )
  );

  slowImages.concat(fastImages).forEach((image) => {
    const isSlow = slowImages.includes(image);
    const offset =
      image.offsetLeft +
      image.parentNode.offsetLeft +
      (!image.parentNode.parentNode.className.includes('park-new__place-container')
        ? image.parentNode.parentNode.offsetLeft
        : 0);

    gsap.from(image, {
      x: isSlow ? 150 : 550,

      scrollTrigger: {
        start: () =>
          container.getBoundingClientRect().top +
          window.scrollY +
          offset -
          (isSlow ? 150 : 550) -
          image.clientWidth,
        end: () => '+=' + window.innerWidth * 1.5,
        scrub: 0.3
      }
    });
  });

  const numbers = document.querySelectorAll('.park-new__numbers-item');
  const numbersContainer = document.querySelector('.park-new__numbers-container');
  const numbersOrder = [1, 4, 3, 6, 2, 5];

  numbers.forEach((number) => {
    const imageIndex = Math.abs(parseInt(number.className.slice(-2)));
    const offset = window.innerWidth * 0.15 * numbersOrder.lastIndexOf(imageIndex);

    let endOffset = 0;

    imageIndex === 4
      ? (endOffset += window.innerWidth * 0.12)
      : imageIndex === 6
      ? (endOffset += window.innerWidth * 0.01)
      : null;

    gsap.to(number, {
      x: -number.clientWidth + endOffset,
      ease: 'none',

      scrollTrigger: {
        trigger: numbersContainer,
        start: () => 'top center-=' + offset,
        end: () => 'bottom top-=' + window.innerWidth * 0.2,
        refreshPriority: -1,
        scrub: 0.3
      }
    });
  });
};

const AnimationsNewComponent = () => {
  if (!isMobile()) {
    pressItemAppear();
    randomAppear();
    aboutAnimation();
    parkAnimation();
    parallaxTransition();
    prompterTransition();
    headerTransition();
    scrollToTransition();
  }

  scrollAppear();
  scrollTransition(150);
};

export const AnimationsNew = () => {
  $(document).ready(() => AnimationsNewComponent());
};
